import type { ProblemDetails } from "@rago/web-shared";
import { API_BASE } from "./api";
import { isLocalMode } from "./appMode";
import { ApiProblemError, auth } from "./authManager";

interface RequestOpts {
  method?: string;
  body?: unknown;
  auth?: boolean;
  /** An explicit Bearer in place of the stored session's: a session this
   * page has not adopted yet (lib/localAccess). A 401 to it is the caller's
   * to judge — the stored session had no part in the request. */
  token?: string;
  signal?: AbortSignal;
}

/**
 * JSON API client for the broker: Bearer session token, problem+json
 * parsing. Hosted calls go credentialed so the API's Set-Cookie (the
 * rago_tv_* cookies) lands; the credential itself is the header. A 401 is
 * not retried — there is nothing to refresh — it is reported to the auth
 * manager, which asks GET /v1/me whether the session is really gone.
 */
export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  const token = opts.token ?? (opts.auth === false ? null : auth.sessionToken());
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
    credentials: isLocalMode ? "omit" : "include",
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  if (res.status === 401 && token && opts.token === undefined) void auth.onUnauthorized(token);

  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => null)) as T | ProblemDetails | null;
  if (!res.ok) {
    throw new ApiProblemError(
      (json as ProblemDetails | null) ?? {
        type: "about:blank",
        title: "Request failed",
        status: res.status,
        code: "internal",
      },
    );
  }
  return json as T;
}
